import { DefaultInlineManagerExtension } from '@blocksuite/affine-inline-preset';
import type { RichText } from '@blocksuite/affine-rich-text';
import {
  ParseDocUrlProvider,
  TelemetryProvider,
} from '@blocksuite/affine-shared/services';
import {
  caretRangeFromPoint,
  getViewportElement,
  isValidUrl,
} from '@blocksuite/affine-shared/utils';
import {
  BaseCellRenderer,
  DATA_VIEW_CELL_PRESENCE_EVENT,
} from '@blocksuite/data-view';
import { IS_MAC } from '@blocksuite/global/env';
import { LinkedPageIcon } from '@blocksuite/icons/lit';
import type { BlockComponent } from '@blocksuite/std';
import { type BlockSnapshot, type DeltaInsert, Text } from '@blocksuite/store';
import { computed, effect, signal } from '@preact/signals-core';
import { property } from 'lit/decorators.js';
import { ref } from 'lit/directives/ref.js';
import { html } from 'lit/static-html.js';

import { EditorHostKey } from '../../context/host-context.js';
import { getSingleDocIdFromText } from '../../utils/title-doc.js';
import { textFromString } from '../text-from-string.js';
import {
  headerAreaIconStyle,
  titleCellStyle,
  titleRichTextStyle,
} from './cell-renderer-css.js';

type DatabaseCellEditorHost = HTMLElement & {
  topContenteditableElement?: BlockComponent | null;
};

const DATABASE_CELL_EDITOR_HOST_SELECTOR =
  'affine-database, affine-edgeless-database';

export class HeaderAreaTextCell extends BaseCellRenderer<Text, string> {
  activity = true;
  private _pendingEditingFocus = false;

  docId$ = signal<string>();

  get host() {
    return this.view.serviceGet(EditorHostKey);
  }

  get inlineEditor() {
    return this.richText$.value?.inlineEditor;
  }

  get inlineManager() {
    return this.host?.std.get(DefaultInlineManagerExtension.identifier);
  }

  get topContenteditableElement() {
    const databaseBlock = this.closest<DatabaseCellEditorHost>(
      DATABASE_CELL_EDITOR_HOST_SELECTOR
    );
    return databaseBlock?.topContenteditableElement;
  }

  get inlineEventSource(): BlockComponent | undefined {
    return this.topContenteditableElement ?? undefined;
  }

  get std() {
    return this.view.serviceGet(EditorHostKey)?.std;
  }

  private readonly _onCopy = (e: ClipboardEvent) => {
    const inlineEditor = this.inlineEditor;
    if (!inlineEditor) return;

    const inlineRange = inlineEditor.getInlineRange();
    if (!inlineRange) return;

    const text = inlineEditor.yTextString.slice(
      inlineRange.index,
      inlineRange.index + inlineRange.length
    );

    e.clipboardData?.setData('text/plain', text);
    e.preventDefault();
    e.stopPropagation();
  };

  private readonly _onCut = (e: ClipboardEvent) => {
    const inlineEditor = this.inlineEditor;
    if (!inlineEditor) return;

    const inlineRange = inlineEditor.getInlineRange();
    if (!inlineRange) return;

    const text = inlineEditor.yTextString.slice(
      inlineRange.index,
      inlineRange.index + inlineRange.length
    );
    inlineEditor.deleteText(inlineRange);
    inlineEditor.setInlineRange({
      index: inlineRange.index,
      length: 0,
    });

    e.clipboardData?.setData('text/plain', text);
    e.preventDefault();
    e.stopPropagation();
  };

  private readonly _onPaste = (e: ClipboardEvent) => {
    const inlineEditor = this.inlineEditor;
    const inlineRange = inlineEditor?.getInlineRange();
    if (!inlineRange) return;
    if (e.clipboardData) {
      try {
        const getDeltas = (snapshot: BlockSnapshot): DeltaInsert[] => {
          // @ts-expect-error FIXME: ts error
          const text = snapshot.props?.text?.delta;
          return text
            ? [...text, ...(snapshot.children?.flatMap(getDeltas) ?? [])]
            : snapshot.children?.flatMap(getDeltas);
        };
        const snapshot = this.std?.clipboard?.readFromClipboard(
          e.clipboardData
        )['BLOCKSUITE/SNAPSHOT'];
        const deltas = (
          JSON.parse(snapshot).snapshot.content as BlockSnapshot[]
        ).flatMap(getDeltas);
        deltas.forEach(delta => this.insertDelta(delta));
        return;
      } catch {
        //
      }
    }
    const text = e.clipboardData
      ?.getData('text/plain')
      ?.replace(/\r?\n|\r/g, '\n');
    if (!text) return;
    e.preventDefault();
    e.stopPropagation();
    if (isValidUrl(text)) {
      const std = this.std;
      const result = std?.getOptional(ParseDocUrlProvider)?.parseDocUrl(text);
      if (result) {
        const text = ' ';
        inlineEditor?.insertText(inlineRange, text, {
          reference: {
            type: 'LinkedPage',
            pageId: result.docId,
            params: {
              blockIds: result.blockIds,
              elementIds: result.elementIds,
              mode: result.mode,
            },
          },
        });
        inlineEditor?.setInlineRange({
          index: inlineRange.index + text.length,
          length: 0,
        });

        // Track when a linked doc is created in database title column
        std?.getOptional(TelemetryProvider)?.track('LinkedDocCreated', {
          module: 'database title cell',
          type: 'paste',
          segment: 'database',
          parentFlavour: 'affine:database',
        });
      } else {
        inlineEditor?.insertText(inlineRange, text, {
          link: text,
        });
        inlineEditor?.setInlineRange({
          index: inlineRange.index + text.length,
          length: 0,
        });
      }
    } else {
      inlineEditor?.insertText(inlineRange, text);
      inlineEditor?.setInlineRange({
        index: inlineRange.index + text.length,
        length: 0,
      });
    }
  };

  insertDelta = (delta: DeltaInsert) => {
    const inlineEditor = this.inlineEditor;
    const range = inlineEditor?.getInlineRange();
    if (!range || !delta.insert) {
      return;
    }
    inlineEditor?.insertText(range, delta.insert, delta.attributes);
    inlineEditor?.setInlineRange({
      index: range.index + delta.insert.length,
      length: 0,
    });
  };

  private readonly _publishCaretPresence = () => {
    if (!this.isEditing$.value) {
      return;
    }
    const inlineRange = this.inlineEditor?.getInlineRange();
    if (!inlineRange) {
      return;
    }
    this.dispatchEvent(
      new CustomEvent(DATA_VIEW_CELL_PRESENCE_EVENT, {
        bubbles: true,
        composed: true,
        detail: {
          caretLength: 0,
          caretOffset: inlineRange.index + inlineRange.length,
          columnId: this.cell.propertyId,
          mode: 'editing',
          rowId: this.cell.rowId,
          viewId: this.view.id,
        },
      })
    );
  };

  override connectedCallback() {
    super.connectedCallback();
    this.classList.add(titleCellStyle);

    const yText = this.value?.yText;
    if (yText) {
      const cb = () => {
        const id = getSingleDocIdFromText(this.value);
        this.docId$.value = id;
      };
      cb();
      if (this.activity) {
        yText.observe(cb);
        this.disposables.add(() => {
          yText.unobserve(cb);
        });
      }
    }

    const selectAll = (e: KeyboardEvent) => {
      if (e.key === 'a' && (IS_MAC ? e.metaKey : e.ctrlKey)) {
        e.stopPropagation();
        e.preventDefault();
        this.inlineEditor?.selectAll();
      }
    };

    this.disposables.addFromEvent(this, 'keydown', selectAll);
    this.disposables.add(
      effect(() => {
        const richText = this.richText$.value;
        if (!richText) {
          return;
        }
        let keydownDisposable: { unsubscribe: () => void } | undefined;
        let unsubscribeCaretPresence: (() => void) | undefined;
        richText.addEventListener('copy', this._onCopy, true);
        richText.addEventListener('cut', this._onCut, true);
        richText.addEventListener('paste', this._onPaste, true);
        richText.updateComplete
          .then(() => {
            if (!this.isConnected || this.richText$.value !== richText) {
              return;
            }
            const inlineEditor = richText.inlineEditor;
            keydownDisposable = inlineEditor?.slots.keydown.subscribe(
              this._handleKeyDown
            );
            unsubscribeCaretPresence = inlineEditor?.inlineRange$.subscribe(
              this._publishCaretPresence
            );
            if (this._pendingEditingFocus && this.isEditing$.value)
              this.afterEnterEditingMode();
            this._publishCaretPresence();
          })
          .catch(console.error);
        return () => {
          richText.removeEventListener('copy', this._onCopy, true);
          richText.removeEventListener('cut', this._onCut, true);
          richText.removeEventListener('paste', this._onPaste, true);
          keydownDisposable?.unsubscribe();
          unsubscribeCaretPresence?.();
        };
      })
    );
    this.disposables.add(
      effect(() => {
        const inlineEditor = this.inlineEditor;
        if (!inlineEditor || !this.isEditing$.value) {
          return;
        }
        inlineEditor.inlineRange$.value;
        this._publishCaretPresence();
      })
    );
  }

  override afterEnterEditingMode() {
    this._pendingEditingFocus = true;
    const inlineEditor = this.inlineEditor;
    if (!inlineEditor) return;
    this._pendingEditingFocus = false;
    const point = this.takeEditingPoint();
    if (!point) {
      inlineEditor.focusEnd();
      return;
    }
    const nativeRange = caretRangeFromPoint(point.clientX, point.clientY);
    const inlineRange = nativeRange
      ? inlineEditor.toInlineRange(nativeRange)
      : null;
    if (inlineRange) {
      inlineEditor.setInlineRange({ index: inlineRange.index, length: 0 });
    } else {
      inlineEditor.focusEnd();
    }
  }

  override beforeExitEditingMode() {
    this._pendingEditingFocus = false;
  }

  override disconnectedCallback() {
    this._pendingEditingFocus = false;
    super.disconnectedCallback();
  }

  private readonly _handleKeyDown = (event: KeyboardEvent) => {
    if (event.key !== 'Escape') {
      if (event.key === 'Tab') {
        event.preventDefault();
        return;
      }
      event.stopPropagation();
    }
  };

  override beforeEnterEditMode() {
    const value = this.value as Text | string | null | undefined;
    if (!value || typeof value === 'string') {
      this.valueSetImmediate(textFromString(value?.toString() ?? ''));
    }
    return true;
  }

  protected override render(): unknown {
    return html`${this.renderIcon()}${this.renderBlockText()}`;
  }

  renderBlockText() {
    const value = this.value;
    if (!(value instanceof Text)) {
      return html` <div class="${titleRichTextStyle}"></div>`;
    }
    return html` <rich-text
      ${ref(this.richText$)}
      data-disable-ask-ai
      data-not-block-text
      .yText="${value}"
      .inlineEventSource="${this.inlineEventSource}"
      .attributesSchema="${this.inlineManager?.getSchema()}"
      .attributeRenderer="${this.inlineManager?.getRenderer()}"
      .embedChecker="${this.inlineManager?.embedChecker}"
      .markdownMatches="${this.inlineManager?.markdownMatches}"
      .readonly="${!this.isEditing$.value}"
      .wrapText="${!this.singleLine}"
      .enableClipboard="${false}"
      .verticalScrollContainerGetter="${() =>
        this.inlineEventSource?.host
          ? getViewportElement(this.inlineEventSource.host)
          : null}"
      data-parent-flavour="affine:database"
      class="${titleRichTextStyle}"
    ></rich-text>`;
  }
  icon$ = computed(() => {
    const iconColumn = this.view.mainProperties$.value.iconColumn;
    if (!iconColumn) return;

    const icon = this.view.cellGetOrCreate(this.cell.rowId, iconColumn).value$
      .value;
    if (!icon) return;
    return icon;
  });
  renderIcon() {
    if (!this.showIcon) {
      return;
    }
    if (this.docId$.value) {
      return html` <div class="${headerAreaIconStyle}">
        ${LinkedPageIcon({})}
      </div>`;
    }
    const icon = this.icon$.value;
    if (!icon) return;

    return html` <div class="${headerAreaIconStyle}">${icon}</div>`;
  }

  private readonly richText$ = signal<RichText>();

  @property({ attribute: false })
  accessor showIcon = false;
}

declare global {
  interface HTMLElementTagNameMap {
    'data-view-header-area-text': HeaderAreaTextCell;
  }
}
